import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Sparkles, Wand2, Plus } from 'lucide-react';
import {
  quantAIProvidersAvailablePublic, portfolioAIAnalyze,
} from '../lib/api';
import AIExecutionDialog from './quant/AIExecutionDialog';

const ACTIONS = [
  {
    template: 'analyze-portfolio',
    label: 'Analizza',
    icon: Sparkles,
    intentTitle: 'Cosa vuoi sapere?',
    intentPlaceholder: 'Es. "concentrami su perché il drawdown di marzo" — oppure lascia vuoto per diagnosi standard',
  },
  {
    template: 'improve-strategy-for-portfolio',
    label: 'Migliora strategia',
    icon: Wand2,
    intentTitle: 'Direzione per il miglioramento (opzionale)',
    intentPlaceholder: 'Es. "voglio stop più stretti", "riduci il turnover" — oppure lascia vuoto',
  },
  {
    template: 'generate-new-strategy-for-portfolio',
    label: 'Genera nuova strategia',
    icon: Plus,
    intentTitle: 'Vincoli o direzione (opzionale)',
    intentPlaceholder: 'Es. "regime-aware", "low-turnover swing su ETF Core" — oppure lascia vuoto',
  },
];

const PortfolioAIActions = ({ portfolio, onComplete }) => {
  const [providers, setProviders] = useState([]);
  const [generatorId, setGeneratorId] = useState(null);
  const [validatorId, setValidatorId] = useState(null);
  const [intentDialog, setIntentDialog] = useState(null);  // { action, text }
  const [execArgs, setExecArgs] = useState(null);

  useEffect(() => {
    quantAIProvidersAvailablePublic()
      .then((r) => {
        const items = (r.data.items || []).filter((p) => p.active);
        setProviders(items);
        if (items.length) {
          setGeneratorId(items[0].id);
          setValidatorId(items[1]?.id || items[0].id);  // cross-validation se possibile
        }
      })
      .catch(() => setProviders([]));
  }, []);

  const hasProvider = !!generatorId;
  const hasStrategy = !!portfolio?.strategy_name;

  const openIntent = (action) => {
    if (!hasProvider) {
      toast.warning('Configura un provider AI in /admin/ai-providers prima');
      return;
    }
    if (action.template === 'improve-strategy-for-portfolio' && !hasStrategy) {
      toast.warning('Il portafoglio non ha una strategia associata — usa "Genera nuova" invece');
      return;
    }
    setIntentDialog({ action, text: '' });
  };

  const launch = () => {
    const { action, text } = intentDialog;
    setExecArgs({
      template: action.template,
      generator_provider_id: generatorId,
      validator_provider_id: validatorId,
      custom_text: text.trim() || null,
      min_score: 50,
    });
    setIntentDialog(null);
  };

  const handleSuccess = (result) => {
    if (result?.save_skipped_reason) {
      toast.warning(`AI ok ma non salvata: ${result.save_skipped_reason}`);
    } else {
      const paths = (result?.saved_paths || []).join(', ');
      toast.success(`AI completata${paths ? ': ' + paths : ''}`);
    }
    onComplete?.(result);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {providers.length > 1 && (
        <Select value={generatorId || ''} onValueChange={setGeneratorId}>
          <SelectTrigger className="w-44 h-9 text-xs">
            <SelectValue placeholder="Provider AI" />
          </SelectTrigger>
          <SelectContent>
            {providers.map((p) => (
              <SelectItem key={p.id} value={p.id} className="text-xs">
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {ACTIONS.map((a) => (
        <Button
          key={a.template}
          variant="outline"
          size="sm"
          onClick={() => openIntent(a)}
          disabled={!hasProvider}
          data-testid={`portfolio-ai-${a.template}`}
        >
          <a.icon className="w-4 h-4 mr-1.5" />
          {a.label}
        </Button>
      ))}

      {/* Intent input dialog */}
      <Dialog open={!!intentDialog} onOpenChange={(v) => !v && setIntentDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {intentDialog?.action?.label} con AI
            </DialogTitle>
            <DialogDescription>
              {intentDialog?.action?.intentTitle}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={intentDialog?.text || ''}
            onChange={(e) => setIntentDialog((d) => d ? { ...d, text: e.target.value } : null)}
            placeholder={intentDialog?.action?.intentPlaceholder}
            rows={4}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIntentDialog(null)}>Annulla</Button>
            <Button onClick={launch}>
              <Sparkles className="w-4 h-4 mr-1.5" />
              Esegui
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Execution dialog (riusato) */}
      <AIExecutionDialog
        open={!!execArgs}
        onOpenChange={(v) => !v && setExecArgs(null)}
        executeArgs={execArgs}
        executeFn={(args) => portfolioAIAnalyze(portfolio.id, args)}
        title="Analisi AI portafoglio"
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default PortfolioAIActions;
